import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { QueryParamsCard } from '../../Prompts/components/PrompPreview/QueryParamsCard';

import { QueryTester } from './QueryTester';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useTestQuery } from '@/hooks/useTestQuery';
import { useUpdatePromptVariable } from '@/hooks/useUpdatePromptVariable';
import {
  FilterPromptVariablesDto,
  PromptVariable,
} from '@/services/PromptsService';

const promptVariableSchema = z.object({
  name: z.string().trim().min(1, 'Variable name is required'),
  query: z.string().trim().min(1, 'Query is required'),
});

type PromptVariableFormData = z.infer<typeof promptVariableSchema>;

interface EditPromptVariableFormProps {
  open: boolean;
  onClose: () => void;
  promptVariable: PromptVariable | null;
  onSuccess?: () => void;
  filter: FilterPromptVariablesDto;
}

export function EditPromptVariableForm({
  open,
  onClose,
  promptVariable,
  onSuccess,
  filter,
}: EditPromptVariableFormProps) {
  const [queryParams, setQueryParams] = useState<
    Record<string, string | number | boolean>
  >({});

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<PromptVariableFormData>({
    resolver: zodResolver(promptVariableSchema),
    defaultValues: {
      name: '',
      query: '',
    },
  });

  const { updatePromptVariable, isUpdating } = useUpdatePromptVariable(filter);
  const { testQuery, isTestingQuery } = useTestQuery();

  const queryValue = watch('query');

  const handlePayload = useCallback(() => {
    // Não faz nada - só para satisfazer a interface
  }, []);

  useEffect(() => {
    if (promptVariable && open) {
      reset({
        name: promptVariable.name,
        query: promptVariable.query,
      });
      setQueryParams({});
    }
  }, [promptVariable, open, reset]);

  async function onSubmit(data: PromptVariableFormData) {
    if (!promptVariable?.id) {
      toast.error('Variable ID is required for update');
      return;
    }

    try {
      await updatePromptVariable({
        id: promptVariable.id,
        data: {
          name: data.name,
          query: data.query,
        },
      });

      toast.success('Prompt variable updated successfully!');
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.error(
        error instanceof AxiosError
          ? error.response?.data?.message
          : 'Failed to update prompt variable. Please try again.',
      );
    }
  }

  const handleClose = () => {
    setQueryParams({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Prompt Variable</DialogTitle>
          <DialogDescription>
            Update the details for this prompt variable.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-6 py-4">
            <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="name">Variable Name</RequiredLabel>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input {...field} placeholder="Enter variable name" />
                )}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.query?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="query">Query</RequiredLabel>
              <Controller
                name="query"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={6}
                    placeholder="Enter your SQL query or data retrieval logic here... Use {{paramName}} for parameters."
                  />
                )}
              />
              <p className="mt-1 text-sm text-gray-500">
                Define how this variable should be populated. Use{' '}
                {`{{paramName}}`} for query parameters.
              </p>
            </FormGroup>

            {queryValue && (
              <QueryParamsCard
                onPayload={handlePayload}
                queryParams={queryParams}
                onQueryParamsChange={setQueryParams}
                query={queryValue}
              />
            )}

            <QueryTester
              query={queryValue}
              queryParams={queryParams}
              isTesting={isTestingQuery}
              onTest={testQuery}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update Variable'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
